import { Flex, HStack, Separator, VStack } from "@chakra-ui/react"
import { useState } from "react"
import ConfigureRange, { ConfigureRangeSkeleton } from "@hydrologiq/shared/reporting/ConfigureRange"
import { Granularity } from "@hydrologiq/common/types/reporting/Granularity"
import { DateTime } from "timezonecomplete"

import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { FaChartColumn, FaTableList } from "react-icons/fa6"
import { ReportingSkeleton } from "./Reporting"
import { ReportingChartSkeleton } from "./ReportingChart"
import { startOfDay, toUserTZ, endOfDay, nowDateTime } from "@hydrologiq/common/helpers/date"
import { type RowType } from "./rows"

export type UsageView = "chart" | "table"

export function UsageSkeleton({ view, title }: { view: UsageView; title?: boolean }) {
  return view === "table" ? <ReportingSkeleton /> : <ReportingChartSkeleton title={title} />
}

interface UsageComponentProps {
  start: DateTime
  end: DateTime
  granularity: Granularity
  view: UsageView
  availableFields?: (keyof RowType)[]
}

function Usage<AdditionalProps extends Record<string, unknown>>({
  start: defaultStart,
  end: defaultEnd,
  UsageComponent,
  additionalProps,
  availableFields,
}: {
  start: DateTime | string
  end?: DateTime | string
  UsageComponent: React.ComponentType<UsageComponentProps & AdditionalProps>
  additionalProps: AdditionalProps
  availableFields?: (keyof RowType)[]
}) {
  const [start, setStart] = useState<DateTime>()
  const [end, setEnd] = useState<DateTime>()

  const [min] = useState<DateTime>(startOfDay(defaultStart ? toUserTZ(defaultStart) : nowDateTime()))
  const [max] = useState<DateTime>(endOfDay(defaultEnd ? toUserTZ(defaultEnd) : nowDateTime()))

  const [granularity, setgranularity] = useState<Granularity>(Granularity.Monthly)
  const [view, setView] = useState<UsageView>("table")

  return (
    <VStack align="stretch">
      <HStack gap={5} height="100%" justify="flex-start" align="flex-end" flexDirection={{ base: "column", lg: "row" }}>
        <VStack align="stretch" width={{ base: "100%", lg: "50%" }}>
          {!min || !max ? (
            <ConfigureRangeSkeleton />
          ) : (
            <ConfigureRange
              min={min}
              max={max}
              callback={(granularity, start, end) => {
                setgranularity(granularity)
                setStart(start)
                setEnd(end)
              }}
            />
          )}
        </VStack>
        <Flex height="100%">
          <SegmentedControl
            variant="hydrologiq_icon"
            items={[
              { value: "table", label: "Table", icon: FaTableList },
              { value: "chart", label: "Chart", icon: FaChartColumn },
            ]}
            onValueChange={(value) => setView(value.value as "chart" | "table")}
            value={view}
          />
        </Flex>
      </HStack>

      {!start || !end ? (
        <UsageSkeleton view={view} />
      ) : (
        <UsageComponent
          start={start}
          end={end}
          granularity={granularity}
          view={view}
          availableFields={availableFields}
          {...additionalProps}
        />
      )}
    </VStack>
  )
}

export default Usage
